import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, ApiError, asList } from '../lib/api';

interface ReminderBand { fromDaysOverdue: number; everyDays: number; }
interface FeeSettings { allowPartialPayment: boolean; lateFeeKind: string; lateFeeValue: number; lateFeeGraceDays: number; lateFeeMaxPaise: number; remindersEnabled: boolean; reminderLadderJson: string; staffEscalationDays: number; }
interface FeeHead { id: string; name: string; code: string | null; isActive: boolean; sortOrder: number; }
interface Defaulter { studentId: string; studentName: string; admissionNumber: string; classLabel: string | null; outstandingPaise: number; earliestDueDate: string | null; overdueInvoices: number; }

const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/// Fee administration: partial-payment policy, late-fee rule, fee heads, and the defaulter report.
/// Finance-gated on the backend.
export function FeeAdminPage({ embedded = false }: { embedded?: boolean }) {
  const [tab, setTab] = useState<'policy' | 'raise' | 'heads' | 'defaulters'>('policy');
  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Fees</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Fee administration</h1>
        </div>
      )}
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn active={tab === 'policy'} onClick={() => setTab('policy')}>Policy</TabBtn>
        <TabBtn active={tab === 'raise'} onClick={() => setTab('raise')}>Raise a fee</TabBtn>
        <TabBtn active={tab === 'heads'} onClick={() => setTab('heads')}>Fee heads</TabBtn>
        <TabBtn active={tab === 'defaulters'} onClick={() => setTab('defaulters')}>Defaulters</TabBtn>
      </div>
      {tab === 'policy' && <PolicyTab />}
      {tab === 'raise' && <RaiseFeeTab />}
      {tab === 'heads' && <HeadsTab />}
      {tab === 'defaulters' && <DefaultersTab />}
    </div>
  );
}

function PolicyTab() {
  const [s, setS] = useState<FeeSettings | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get<FeeSettings>('/api/fees/settings').then(setS).catch(() => setS(null)); }, []);

  const save = async () => {
    if (!s) return;
    setBusy(true); setMsg(null);
    try {
      const saved = await api.put<FeeSettings>('/api/fees/settings', s);
      setS(saved); setMsg('Saved.');
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  if (!s) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  const rupeeField = s.lateFeeKind === 'Percent';

  return (
    <div className="card" style={{ padding: 20, maxWidth: 560 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, cursor: 'pointer' }}>
        <input type="checkbox" checked={s.allowPartialPayment} onChange={(e) => setS({ ...s, allowPartialPayment: e.target.checked })} />
        <span>
          <strong>Allow partial fee payment</strong>
          <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>On: families can pay in parts / installments (PU-style). Off: the full balance must be cleared in one payment.</div>
        </span>
      </label>

      <div style={{ height: 1, background: 'var(--line)', margin: '4px 0 16px' }} />
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Late fee on overdue invoices</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <label className="label">Type</label>
          <select className="input" value={s.lateFeeKind} onChange={(e) => setS({ ...s, lateFeeKind: e.target.value })}>
            <option value="None">None</option>
            <option value="Flat">Flat (once)</option>
            <option value="PerDay">Per day</option>
            <option value="Percent">Percent of balance</option>
          </select>
        </div>
        {s.lateFeeKind !== 'None' && (
          <>
            <div>
              <label className="label">{rupeeField ? 'Percent (basis pts, 200=2%)' : 'Amount (paise)'}</label>
              <input className="input" type="number" value={s.lateFeeValue} onChange={(e) => setS({ ...s, lateFeeValue: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Grace days</label>
              <input className="input" type="number" value={s.lateFeeGraceDays} onChange={(e) => setS({ ...s, lateFeeGraceDays: Number(e.target.value) })} style={{ width: 90 }} />
            </div>
            <div>
              <label className="label">Cap (paise, 0=none)</label>
              <input className="input" type="number" value={s.lateFeeMaxPaise} onChange={(e) => setS({ ...s, lateFeeMaxPaise: Number(e.target.value) })} />
            </div>
          </>
        )}
      </div>

      <div style={{ height: 1, background: 'var(--line)', margin: '16px 0' }} />
      <ReminderSettings settings={s} onSaved={(next) => { setS(next); setMsg('Reminder cadence saved.'); }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <button className="btn btn-accent" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save policy'}</button>
        {msg && <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{msg}</span>}
      </div>
    </div>
  );
}

const DEFAULT_LADDER: ReminderBand[] = [
  { fromDaysOverdue: 1, everyDays: 30 },
  { fromDaysOverdue: 60, everyDays: 7 },
  { fromDaysOverdue: 80, everyDays: 3 },
  { fromDaysOverdue: 88, everyDays: 1 },
];

function parseLadder(json: string): ReminderBand[] {
  try { const a = JSON.parse(json); if (Array.isArray(a) && a.length) return a; } catch { /* */ }
  return DEFAULT_LADDER;
}

function ReminderSettings({ settings, onSaved }: { settings: FeeSettings; onSaved: (s: FeeSettings) => void }) {
  const [enabled, setEnabled] = useState(settings.remindersEnabled);
  const [staffDays, setStaffDays] = useState(settings.staffEscalationDays);
  const [ladder, setLadder] = useState<ReminderBand[]>(parseLadder(settings.reminderLadderJson));
  const [busy, setBusy] = useState(false);

  const setBand = (i: number, k: keyof ReminderBand, v: number) =>
    setLadder(ladder.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const sorted = [...ladder].sort((a, b) => a.fromDaysOverdue - b.fromDaysOverdue);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.put<FeeSettings>('/api/fees/reminder-settings', {
        enabled,
        ladder: ladder.filter((b) => b.fromDaysOverdue >= 0 && b.everyDays >= 1),
        staffEscalationDays: staffDays,
      });
      onSaved(saved);
    } catch { /* */ } finally { setBusy(false); }
  };

  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>
          <strong>Automatic overdue reminders</strong>
          <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            Send fee reminders to parents on an escalating schedule — more often the longer a fee stays unpaid.
            Partly-paid invoices keep getting reminders until fully cleared.
          </div>
        </span>
      </label>

      {enabled && (
        <div style={{ marginLeft: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Reminder ladder</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
            Read each row as: “once a fee is at least N days overdue, remind every M days.”
            Later rows take over as the fee ages — so the cadence naturally tightens.
          </div>

          <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
            {ladder.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>From</span>
                <input className="input" type="number" value={b.fromDaysOverdue} style={{ width: 80 }}
                  onChange={(e) => setBand(i, 'fromDaysOverdue', Number(e.target.value))} />
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>days overdue, remind every</span>
                <input className="input" type="number" value={b.everyDays} style={{ width: 70 }}
                  onChange={(e) => setBand(i, 'everyDays', Number(e.target.value))} />
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>days</span>
                <button className="btn" style={{ padding: '2px 8px' }} onClick={() => setLadder(ladder.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="btn" onClick={() => setLadder([...ladder, { fromDaysOverdue: 0, everyDays: 7 }])}>+ Add rung</button>
            <button className="btn" onClick={() => setLadder(DEFAULT_LADDER)}>Reset to default</button>
          </div>

          <div className="card" style={{ padding: 12, background: 'var(--surface-2,#f6f6f4)', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>In plain words</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ink-2)' }}>
              {sorted.map((b, i) => (
                <li key={i}>From {b.fromDaysOverdue} day{b.fromDaysOverdue === 1 ? '' : 's'} overdue → {b.everyDays === 1 ? 'every day' : `every ${b.everyDays} days`}</li>
              ))}
            </ul>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="label">Alert staff once a fee is this many days overdue (0 = never)</label>
            <input className="input" type="number" value={staffDays} style={{ width: 110 }}
              onChange={(e) => setStaffDays(Number(e.target.value))} />
          </div>

          <button className="btn btn-accent" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save reminder cadence'}</button>
        </div>
      )}
    </div>
  );
}

function RaiseFeeTab() {
  const [feeTypes, setFeeTypes] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string; institutionId: string; classLabel?: string | null }>>([]);
  const [target, setTarget] = useState<{ id: string; fullName: string; institutionId: string } | null>(null);
  const [feeType, setFeeType] = useState('Exam');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get<string[]>('/api/fees/fee-types').then((r) => setFeeTypes(asList(r))).catch(() => setFeeTypes([])); }, []);

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=8`))); } catch { setResults([]); }
  };

  const raise = async () => {
    if (!target) { setMsg('Pick a student first.'); return; }
    const paise = Math.round(Number(amount) * 100);
    if (!paise || paise <= 0) { setMsg('Enter a valid amount.'); return; }
    setBusy(true); setMsg(null);
    try {
      await api.post('/api/fees/adhoc', {
        studentId: target.id,
        institutionId: target.institutionId,
        academicYear: currentAcademicYear(),
        feeType,
        feeTypeLabel: feeType === 'Other' ? (label.trim() || null) : (label.trim() || null),
        amountPaise: paise,
        lineLabel: label.trim() || null,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      });
      setMsg(`Fee raised for ${target.fullName}. It's now in their payments to pay.`);
      setAmount(''); setLabel('');
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not raise the fee.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 20, maxWidth: 560 }}>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
        Raise a one-off fee of any type for a student — a bus fee, an exam fee, an activity fee, anything.
        It appears in the parent's Payments list for them to pay online.
      </div>

      {!target ? (
        <>
          <label className="label">Find the student</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()} placeholder="Name or admission number" />
            <button className="btn" onClick={doSearch}>Search</button>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {results.map((s) => (
              <button key={s.id} className="btn" style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                onClick={() => setTarget({ id: s.id, fullName: s.fullName, institutionId: s.institutionId })}>
                {s.fullName} · {s.admissionNumber}{s.classLabel ? ` · ${s.classLabel}` : ''}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}><strong>{target.fullName}</strong></div>
            <button className="btn" onClick={() => setTarget(null)}>Change</button>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="label">Fee type</label>
              <select className="input" value={feeType} onChange={(e) => setFeeType(e.target.value)}>
                {feeTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="label">Amount (₹)</label>
              <input className="input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="label">{feeType === 'Other' ? 'Fee name (shown to parent) *' : 'Custom label (optional)'}</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder={feeType === 'Other' ? 'e.g. Annual Day contribution' : 'e.g. Term-2 bus fee'} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="label">Due date (optional)</label>
            <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={{ width: 180 }} />
          </div>

          <button className="btn btn-accent" disabled={busy} onClick={raise}>{busy ? 'Raising…' : 'Raise fee'}</button>
        </>
      )}

      {msg && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--ink-2)' }}>{msg}</div>}
    </div>
  );
}

function currentAcademicYear(): string {
  const now = new Date();
  const y = now.getFullYear();
  // Indian academic year starts ~June; before June, we're still in the previous year's session.
  return now.getMonth() >= 5 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function HeadsTab() {
  const [rows, setRows] = useState<FeeHead[]>([]);
  const [name, setName] = useState(''); const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get<FeeHead[]>('/api/fees/heads?includeInactive=true').then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post('/api/fees/heads', { name: name.trim(), code: code.trim() || null, sortOrder: rows.length }); setName(''); setCode(''); load(); }
    finally { setBusy(false); }
  };
  const toggle = async (h: FeeHead) => { await api.post(`/api/fees/heads/${h.id}/${h.isActive ? 'deactivate' : 'reactivate'}`); load(); };

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Add a fee head</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tuition" style={{ flex: 1 }} />
          <input className="input mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="TUI" style={{ width: 100 }} />
          <button className="btn btn-accent" disabled={!name.trim() || busy} onClick={add}>Add</button>
        </div>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {rows.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>No fee heads yet.</div> : rows.map((h) => (
          <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--line-2)' }}>
            <strong style={{ opacity: h.isActive ? 1 : 0.5 }}>{h.name}</strong>
            {h.code && <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{h.code}</span>}
            {!h.isActive && <span className="pill pill-muted" style={{ fontSize: 11 }}>Inactive</span>}
            <button className="btn" style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 12 }} onClick={() => toggle(h)}>{h.isActive ? 'Deactivate' : 'Reactivate'}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DefaultersTab() {
  const [rows, setRows] = useState<Defaulter[] | null>(null);
  useEffect(() => { api.get<Defaulter[]>('/api/fees/defaulters').then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  const total = rows.reduce((a, r) => a + r.outstandingPaise, 0);
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div className="card" style={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase' }}>Total overdue</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--danger,#C1272D)' }}>{rupees(total)}</div>
        </div>
        <div className="card" style={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase' }}>Students</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{rows.length}</div>
        </div>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
            <Th>Student</Th><Th>Class</Th><Th>Overdue since</Th><Th align="right">Invoices</Th><Th align="right">Outstanding</Th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><Td colSpan={5} muted>No overdue fees. 🎉</Td></tr>}
            {rows.map((r) => (
              <tr key={r.studentId} style={{ borderBottom: '1px solid var(--line-2)' }}>
                <Td><strong>{r.studentName}</strong> <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.admissionNumber}</span></Td>
                <Td>{r.classLabel ?? '—'}</Td>
                <Td>{r.earliestDueDate ?? '—'}</Td>
                <Td align="right">{r.overdueInvoices}</Td>
                <Td align="right"><strong style={{ color: 'var(--danger,#C1272D)' }}>{rupees(r.outstandingPaise)}</strong></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14, color: active ? 'var(--ink)' : 'var(--ink-3)', fontWeight: active ? 600 : 400, borderBottom: active ? '2px solid var(--accent,#C1272D)' : '2px solid transparent' }}>{children}</button>;
}
function Th({ children, align }: { children?: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, colSpan, muted, align }: { children?: ReactNode; colSpan?: number; muted?: boolean; align?: 'right' }) {
  return <td colSpan={colSpan} style={{ padding: '10px 14px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : (align ?? 'left'), fontSize: 13 }}>{children}</td>;
}
